/**
 * Mock "completed survey" data for the Survey Results screens (VER-109).
 *
 * FRONTEND-ONLY, NO API. This module fabricates realistic respondent answer-sets for
 * the seed surveys so the Results list + detail pages have finished surveys to show
 * under the admin login. Responses are generated by a DETERMINISTIC seeded PRNG
 * (seeded from each survey's code) so the numbers are stable across reloads — no
 * Math.random, no Date.now. Swap this module for a real endpoint later without
 * touching the pages (they consume aggregateSurvey()).
 */
import type { ScaleOption, SurveyDefinition, SurveyQuestion, SurveyResponseValues } from "../builder/survey.types";
import { allQuestions, effectiveOptions, questionBounds } from "../builder/survey.types";
import { resolveVisibility } from "../builder/branching";
import { PROGRAMME_EXPERIENCE_SURVEY, SESSION_FEEDBACK_SURVEY, WELLBEING_CHECKIN_SURVEY } from "../builder/demoSurveys";

export type CompletedSurvey = {
    /** Result id used in the URL (/survey/results/:id). */
    id: string;
    survey: SurveyDefinition;
    /** Programme / context this run belonged to (display only). */
    programmeName: string;
    /** ISO date the survey window closed. */
    completedAt: string;
    responses: SurveyResponseValues[];
};

// ── Deterministic PRNG (mulberry32) ──────────────────────────────────────────
const hashSeed = (text: string): number => {
    let h = 2166136261;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
};

const mulberry32 = (seed: number): (() => number) => {
    let a = seed;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};

const TEXT_POOL = [
    "Really enjoyed the group discussion this week.",
    "The facilitator made everyone feel welcome.",
    "Found it hard to open up at first but it got easier.",
    "The stories gave me a lot to think about.",
    "Would like a bit more time for the activities.",
    "Feeling more connected to the others in the group.",
    "A tough week but talking about it helped.",
    "Looking forward to the next session.",
];

/** Weighted pick from options, biased toward higher values (positive-leaning mock). */
const pickChoiceValue = (options: ScaleOption[], rand: () => number): number => {
    const min = Math.min(...options.map((o) => o.value));
    const weights = options.map((o) => o.value - min + 1); // higher value → more likely
    const total = weights.reduce((a, b) => a + b, 0);
    let r = rand() * total;
    for (let i = 0; i < options.length; i++) {
        r -= weights[i];
        if (r <= 0) return options[i].value;
    }
    return options[options.length - 1].value;
};

const generateAnswer = (
    survey: SurveyDefinition,
    question: SurveyQuestion,
    rand: () => number,
): SurveyResponseValues[string] => {
    switch (question.type) {
        case "likert":
        case "single":
            return pickChoiceValue(effectiveOptions(survey, question), rand);
        case "multi": {
            const options = effectiveOptions(survey, question);
            const selected = options.filter(() => rand() < 0.35).map((o) => o.value);
            return selected; // may be empty → treated as unanswered
        }
        case "nps": {
            const r = rand();
            if (r < 0.5) return 9 + Math.floor(rand() * 2); // promoters 9–10
            if (r < 0.8) return 7 + Math.floor(rand() * 2); // passives 7–8
            return Math.floor(rand() * 7); // detractors 0–6
        }
        case "vas": {
            const { min, max } = questionBounds(survey, question);
            // Bias toward the upper end of the range.
            const t = Math.pow(rand(), 0.6);
            return Math.round(min + (max - min) * t);
        }
        case "freeText":
            return rand() < 0.5 ? "" : TEXT_POOL[Math.floor(rand() * TEXT_POOL.length)];
    }
};

/** One respondent's answer-set, with branch-hidden questions left unanswered. */
const generateResponse = (survey: SurveyDefinition, rand: () => number): SurveyResponseValues => {
    const values: SurveyResponseValues = {};
    for (const question of allQuestions(survey)) {
        values[question.id] = generateAnswer(survey, question, rand);
    }
    // Realism: drop answers for questions hidden by branching given this response.
    const { hiddenQuestionIds } = resolveVisibility(survey, values);
    for (const id of hiddenQuestionIds) {
        delete values[id];
    }
    return values;
};

const generateResponses = (survey: SurveyDefinition, count: number): SurveyResponseValues[] => {
    const rand = mulberry32(hashSeed(survey.code));
    return Array.from({ length: count }, () => generateResponse(survey, rand));
};

const completed = (
    id: string,
    survey: SurveyDefinition,
    programmeName: string,
    completedAt: string,
    respondentCount: number,
): CompletedSurvey => ({
    id,
    survey,
    programmeName,
    completedAt,
    responses: generateResponses(survey, respondentCount),
});

/** The mock set of finished surveys shown on the Results screens. */
export const COMPLETED_SURVEYS: CompletedSurvey[] = [
    completed("who5-spring", SESSION_FEEDBACK_SURVEY, "Adult Wellbeing — Spring cohort", "2026-06-28", 24),
    completed("pexp-spring", PROGRAMME_EXPERIENCE_SURVEY, "Adult Wellbeing — Spring cohort", "2026-06-28", 22),
    completed("sessfb-week6", SESSION_FEEDBACK_SURVEY, "Community Programme — Session 6", "2026-06-25", 18),
    completed("wbchk-week4", WELLBEING_CHECKIN_SURVEY, "Youth Group — Weekly check-in", "2026-06-20", 15),
    completed("ucla-autumn", PROGRAMME_EXPERIENCE_SURVEY, "Loneliness Pilot — Autumn cohort", "2026-05-30", 12),
];

export const findCompletedSurvey = (id: string): CompletedSurvey | undefined =>
    COMPLETED_SURVEYS.find((c) => c.id === id);
